import { Icon, Link, Stack, Text, useTheme } from "@fluentui/react";
import { ECommentStatus, IComment, ICommentRouteRef, ICommentRouteType } from "model/FlexibleAttributeModel";
import { useMemo, useState } from "react";
import { ACTION_LINK_STYLES, buildBreadcrumbItems, getEntityLocationNode, getLeafLocationNode, onRenderLocationTrail, ROUTE_TYPE_LABELS } from "./CommentReviewPanel";


export interface ICommentCardProps {
    comment: IComment;
    onToggleResolved?: (comment: IComment) => void;
    onEditComment?: (comment: IComment) => void;
    onNavigate?: (rootLocation: ICommentRouteRef) => void;
}

export const CommentCard: React.FC<ICommentCardProps> = ({ comment, onToggleResolved, onEditComment, onNavigate }) => {
    const [showRoute, setShowRoute] = useState(false);
    const items = buildBreadcrumbItems(comment.location);
    const leafLocation = getLeafLocationNode(comment.location);
    const entityLocation = getEntityLocationNode(comment.location);
    const cardHeader = leafLocation.name || ROUTE_TYPE_LABELS[leafLocation.type as ICommentRouteType] || 'Comment';
    const entityHeader = entityLocation?.name && entityLocation !== leafLocation ? entityLocation.name : undefined;

    const theme = useTheme();

    const styleBox: React.CSSProperties = useMemo(() => ({
        border: `1px solid ${theme.semanticColors.variantBorder}`,
        borderLeft: comment.status === ECommentStatus.New
            ? "4px solid #ff8f00"
            : comment.status === ECommentStatus.Resolved
                ? "4px solid #1e8e3e"
                : "4px solid transparent",
        borderRadius: "6px",
        boxShadow: theme.effects.elevation4,
        padding: "10px",
        backgroundColor: theme.semanticColors.menuBackground,
    }), [theme, comment.status]);


    const isForGroupConfiguration = comment.location.type === ICommentRouteType.root &&
        !comment.location.next;

    return (
        <div
            style={comment.status === ECommentStatus.Resolved ? { ...styleBox, opacity: 0.6 } : styleBox}
            onMouseLeave={() => setShowRoute(false)}
        >
            <Stack tokens={{ childrenGap: "15px" }}>
                <Stack
                    tokens={{ childrenGap: 2 }}
                    styles={{ root: { cursor: "pointer", backgroundColor: theme.palette.neutralLighter, padding: "4px 6px", borderRadius: "4px" } }}
                    onClick={() => setShowRoute(true)}
                >
                    <Text styles={{ root: { fontWeight: 600 } }}>{cardHeader}</Text>
                    {entityHeader && (
                        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>{entityHeader}</Text>
                    )}
                </Stack>
                <Text>{comment.comment}</Text>

                <Stack horizontal horizontalAlign="end" verticalAlign="center" tokens={{ childrenGap: 12 }}>
                    {!isForGroupConfiguration && onEditComment && (
                        <Link
                            styles={ACTION_LINK_STYLES}
                            onClick={() => onEditComment?.(comment)}
                        >
                            <Icon iconName="Edit" />
                            <span>Edit</span>
                        </Link>
                    )}
                    <Link
                        styles={ACTION_LINK_STYLES}
                        onClick={() => setShowRoute((prev) => !prev)}
                    >
                        <Icon iconName={showRoute ? "ChevronUp" : "MapPin"} />
                        <span>{showRoute ? "Hide" : "Location"}</span>
                    </Link>

                    <Link
                        styles={ACTION_LINK_STYLES}
                        onClick={() => onToggleResolved?.(comment)}
                    >
                        <Icon iconName={comment.status === ECommentStatus.Resolved ? "CheckMark" : "Checkbox"} />
                        <span>Resolved</span>
                    </Link>

                    {!isForGroupConfiguration && onNavigate && (
                        <Link
                            styles={ACTION_LINK_STYLES}
                            onClick={() => onNavigate?.(comment.location)}
                        >
                            <Icon iconName="Nav2DMapView" />
                            <span>Navigate</span>
                        </Link>
                    )}
                </Stack>

                <div
                    style={{
                        maxHeight: showRoute ? "200px" : "0px",
                        opacity: showRoute ? 1 : 0,
                        overflow: "hidden",
                        transition: "max-height 0.3s ease, opacity 0.3s ease",
                    }}
                >
                    {onRenderLocationTrail(items)}
                </div>
            </Stack>
        </div>
    );
};
